import { select, Selection } from "d3";
import NightingaleElement, {
  withDimensions,
  withPosition,
  withMargin,
  withResizable,
  withHighlight,
  withManager,
  withZoom,
  bindEvents,
  customElementOnce,
} from "@nightingale-elements/nightingale-new-core";

// import _includes from "lodash-es/includes";
// import _groupBy from "lodash-es/groupBy";
// import _union from "lodash-es/union";
// import _intersection from "lodash-es/intersection";

import { html } from "lit";
import { property } from "lit/decorators.js";

import FeatureShape, { Shapes } from "./FeatureShape";
import NonOverlappingLayout from "./NonOverlappingLayout";
import DefaultLayout from "./DefaultLayout";
import { getShapeByType, getColorByType } from "./ConfigHelper";

export type FeatureLocation = {
  fragments: Array<{
    start: number;
    end: number;
  }>;
};

type PTM = {
  name: string;
  position: number;
  sources: string[];
  dbReferences: DBReference[];
};

type DBReference = {
  id: string;
  properties: Properties;
};

type Properties = {
  "Pubmed ID": string;
  "PSM Score": string;
  "Dataset ID": string;
  "Site q value": string;
  "Universal Spectrum Id": string;
  "PSM Count (0.05 gFLR)": string;
  "Confidence score": "Gold" | "Silver" | "Bronze";
  "Final site probability": string;
  "Organism part": string;
  Proforma: string;
};

export type Feature = {
  accession: string;
  color?: string;
  fill?: string;
  shape?: Shapes;
  tooltipContent?: string;
  type?: string;
  locations?: Array<FeatureLocation>;
  feature?: Feature;
  start?: number;
  end?: number;
  opacity?: number;
  ptms?: Array<PTM>;
};

// TODO: height is not triggering a full redrawn when is changed after first render
const ATTRIBUTES_THAT_TRIGGER_REFRESH = ["length", "width", "height"];

@customElementOnce("nightingale-track")
class NightingaleTrack extends withManager(
  withZoom(
    withResizable(
      withMargin(
        withPosition(withDimensions(withHighlight(NightingaleElement)))
      )
    )
  )
) {
  @property({ type: String })
  color?: string | null;
  @property({ type: String })
  shape?: string | null;
  @property({ type: String })
  layout?: "non-overlapping" | "default";

  protected featureShape = new FeatureShape();
  protected layoutObj?: DefaultLayout | NonOverlappingLayout;
  #originalData: Feature[] = [];
  #data: Feature[] = [];
  filters = null;

  protected seqG?: Selection<
    SVGGElement,
    unknown,
    HTMLElement | SVGElement | null,
    unknown
  >;
  #highlighted?: Selection<
    SVGGElement,
    unknown,
    HTMLElement | SVGElement | null,
    unknown
  >;
  protected margins?: Selection<
    SVGGElement,
    unknown,
    HTMLElement | SVGElement | null,
    unknown
  >;

  getLayout() {
    if (String(this.layout).toLowerCase() === "non-overlapping")
      return new NonOverlappingLayout({
        layoutHeight: this.height,
      });
    return new DefaultLayout({
      layoutHeight: this.height,
      margin: {
        top: this["margin-top"],
        bottom: this["margin-bottom"],
        left: this["margin-left"],
        right: this["margin-right"],
      },
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.layoutObj = this.getLayout();
    if (this.#data) this.createTrack();

    // TODO: re-enable when the dataloadre is implemented
    // this.addEventListener("load", (e) => {
    //   if (_includes(this.children, e.target)) {
    //     this.data = e.detail.payload;
    //   }
    // });
  }

  static normalizeLocations(data: Feature[]) {
    return data.map((obj) => {
      const { locations, start, end } = obj;
      return locations
        ? obj
        : Object.assign(obj, {
            locations: [
              {
                fragments: [
                  {
                    start,
                    end,
                  },
                ],
              },
            ],
          });
    });
  }

  processData(data: Feature[]) {
    this.#originalData = NightingaleTrack.normalizeLocations(data);
  }

  set data(data: Feature[]) {
    this.processData(data);
    this.applyFilters();
    this.layoutObj = this.getLayout();
    this.createTrack();
  }
  get data() {
    return this.#data;
  }
  // TODO: re-enable filters
  // set filters(filters) {
  //   this._filters = filters;
  //   this.applyFilters();
  //   this.createTrack();
  // }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (
      ATTRIBUTES_THAT_TRIGGER_REFRESH.includes(name) ||
      name.startsWith("margin-")
    ) {
      this.applyFilters();
      this.layoutObj = this.getLayout();
      this.createTrack();
    }
  }

  #getPTMResidueShape(f: PTM & { feature: Feature; position: number }) {
    let ptmLength = 1;
    // For longer proteins, the PTMs have to be shown prominent in the first look
    if (this.length && this.length > 500) {
      ptmLength = this.getSingleBaseWidth() < 4 ? 4 : 1;
    }
    return this.featureShape.getFeatureShape(
      this.getSingleBaseWidth() / 2, // Halve the width of the ptm residue to distinguish between each other if one follows next closely
      this.layoutObj?.getFeatureHeight(f) || 0,
      ptmLength,
      this.getShape(f)
    );
  }

  protected getFeatureColor(f: Feature | { feature: Feature }): string {
    const defaultColor = "gray";
    if ((f as Feature).color) {
      return (f as Feature).color || defaultColor;
    }
    if ((f as { feature: Feature })?.feature?.color) {
      return (f as { feature: Feature })?.feature?.color || defaultColor;
    }
    if (this.color) {
      return this.color;
    }
    if ((f as Feature).type) {
      return getColorByType((f as Feature).type as string);
    }
    if ((f as { feature: Feature })?.feature?.type) {
      return getColorByType((f as { feature: Feature }).feature.type as string);
    }
    return defaultColor;
  }

  protected getFeatureFillColor(f: Feature | { feature: Feature }) {
    const defaultColor = "gray";
    if ((f as Feature).fill) {
      return (f as Feature).fill || defaultColor;
    }
    if ((f as { feature: Feature })?.feature?.fill) {
      return (f as { feature: Feature }).feature.fill || defaultColor;
    }
    return this.getFeatureColor(f);
  }

  protected getShape(f: Feature | { feature: Feature }): Shapes {
    const defaultShape = "rectangle";
    if ((f as Feature).shape) {
      return (f as Feature).shape || defaultShape;
    }
    if ((f as { feature: Feature })?.feature?.shape) {
      return (f as { feature: Feature }).feature.shape || defaultShape;
    }
    if (this.shape) {
      return this.shape as Shapes;
    }
    if ((f as Feature).type) {
      return getShapeByType((f as Feature).type as string) as Shapes;
    }
    if ((f as { feature: Feature }).feature?.type) {
      return getShapeByType(
        (f as { feature: Feature }).feature.type as string
      ) as Shapes;
    }
    return defaultShape;
  }

  protected createTrack() {
    if (!this.#data) {
      return;
    }
    this.layoutObj?.init(this.#data);

    this.svg?.selectAll("g").remove();

    this.svg = select(this as unknown as NightingaleElement)
      .selectAll<SVGSVGElement, unknown>("svg")
      .attr("width", this.width)
      .attr("height", this.height);

    if (!this.svg) return;
    this.seqG = this.svg.append("g").attr("class", "sequence-features");
    this.createFeatures();
    this.#highlighted = this.svg.append("g").attr("class", "highlighted");
    this.margins = this.svg.append("g").attr("class", "margin");
  }

  protected createFeatures() {
    if (!this.seqG) return;
    const featuresG = this.seqG.selectAll("g.feature-group").data(this.#data);

    const locationsG = featuresG
      .enter()
      .append("g")
      .attr("class", "feature-group")
      .attr("id", (d) => `g_${d.accession}`)
      .selectAll("g.location-group")
      .data((d) =>
        (d.locations || []).map((loc) =>
          Object.assign({}, loc, {
            feature: d,
          })
        )
      )
      .enter()
      .append("g")
      .attr("class", "location-group");

    const fragmentGroup = locationsG
      .selectAll("g.fragment-group")
      .data((d) =>
        d.fragments.map((loc) =>
          Object.assign({}, loc, {
            feature: d.feature,
          })
        )
      )
      .enter()
      .append("g")
      .attr("class", "fragment-group");

    fragmentGroup
      .append("path")
      .attr("class", (f) => `${this.getShape(f)} feature`)
      .attr("d", (f) =>
        this.featureShape.getFeatureShape(
          this.getSingleBaseWidth(),
          this.layoutObj?.getFeatureHeight() || 0,
          f.end ? f.end - f.start + 1 : 1,
          this.getShape(f)
        )
      )
      .attr(
        "transform",
        (f) =>
          `translate(${this.getXFromSeqPosition(f.start)},${
            this.layoutObj?.getFeatureYPos(f.feature) || 0
          })`
      )
      .style("fill", (f) => this.getFeatureFillColor(f))
      .attr("stroke", (f) => this.getFeatureColor(f))
      .style("fill-opacity", ({ feature }) =>
        feature.opacity ? feature.opacity : 0.9
      )
      .style("stroke-opacity", ({ feature }) =>
        feature.opacity ? feature.opacity : 0.9
      );

    const residueGroup = fragmentGroup
      .selectAll("g.residue-group")
      .data((d) =>
        d.feature.ptms
          ? [
              d.feature.ptms?.map((ptm) =>
                Object.assign({}, ptm, {
                  feature: d.feature,
                  position: ptm.position,
                })
              ),
            ]
          : []
      )
      .enter()
      .append("g")
      .attr("class", "residue-group");

    fragmentGroup
      .append("rect")
      .attr("class", "outer-rectangle feature")
      .attr("width", (f) =>
        Math.max(
          0,
          this.getSingleBaseWidth() * (f.end ? f.end - f.start + 1 : 1)
        )
      )
      .attr("height", this.layoutObj?.getFeatureHeight() || 0)
      .attr(
        "transform",
        (f) =>
          `translate(${this.getXFromSeqPosition(f.start)},${
            this.layoutObj?.getFeatureYPos(f.feature) || 0
          })`
      )
      .style("fill", "transparent")
      .attr("stroke", "transparent")
      .call(bindEvents, this);

    residueGroup
      .selectAll("g.residue")
      .data((d) => d)
      .enter()
      .append("path")
      .attr("class", (f) => `${this.getShape(f)} residue`)
      .attr("d", (f) => this.#getPTMResidueShape(f))
      .attr(
        "transform",
        (f) =>
          // It is placed in the middle of a single bandwidth
          `translate(${
            this.getXFromSeqPosition(
              Number(f.feature.start) + Number(f.position) - 1
            ) +
            this.getSingleBaseWidth() / 4
          },
          ${this.layoutObj?.getFeatureYPos(f.feature)})`
      )
      .attr("fill", getColorByType("PTM"))
      .style("fill-opacity", ({ feature }) =>
        feature.opacity ? feature.opacity : 0.9
      )
      .style("stroke-opacity", ({ feature }) => {
        return feature.opacity ? feature.opacity : 0.9;
      })
      .call(bindEvents, this);
  }

  private applyFilters() {
    if ((this.filters || []).length <= 0) {
      this.#data = this.#originalData;
      return;
    }
    // TODO: re-enable filters
    //   // Filters are OR-ed within a category and AND-ed between categories
    //   const groupedFilters = _groupBy(this._filters, "category");
    //   const filteredGroups = Object.values(groupedFilters).map((filterGroup) => {
    //     const filteredData = filterGroup.map((filterItem) =>
    //       filterItem.filterFn(this.#originalData)
    //     );
    //     return _union(...filteredData);
    //   });
    //   const intersection = _intersection(...filteredGroups);
    //   this._data = intersection;
  }

  refresh() {
    if (this.xScale && this.seqG) {
      const fragmentG = this.seqG.selectAll("g.fragment-group").data(
        this.#data.reduce(
          (acc: unknown[], f) =>
            acc.concat(
              (f.locations || []).reduce(
                (acc2: unknown[], e) =>
                  acc2.concat(
                    e.fragments.map((loc) =>
                      Object.assign({}, loc, {
                        feature: f,
                      })
                    )
                  ),
                []
              )
            ),
          []
        )
      );

      const residueG = this.seqG.selectAll("g.residue-group").data(
        this.#data.reduce(
          (featureAcc: unknown[], f) =>
            featureAcc.concat([
              f.ptms?.reduce(
                (ptmAcc: unknown[]) =>
                  ptmAcc.concat((ptm: PTM) =>
                    Object.assign({}, ptm, {
                      feature: f,
                      position: ptm.position,
                    })
                  ),
                []
              ),
            ]),
          []
        )
      );

      fragmentG
        .selectAll<SVGPathElement, Feature>("path.feature")
        .attr("d", (f) =>
          this.featureShape.getFeatureShape(
            this.getSingleBaseWidth(),
            this.layoutObj?.getFeatureHeight() || 0,
            f?.end && f?.start ? f.end - f.start + 1 : 1,
            this.getShape(f)
          )
        )
        .attr(
          "transform",
          (f) =>
            `translate(${this.getXFromSeqPosition(
              f.start || 0
            )},${this.layoutObj?.getFeatureYPos(f.feature as Feature)})`
        );

      residueG
        .selectAll<SVGPathElement, PTM & { feature: Feature; position: number; }>("path.residue")
        .attr("d", (f) => this.#getPTMResidueShape(f))
        .attr(
          "transform",
          (f) =>
            `translate(${
              this.getXFromSeqPosition(
                Number(f.feature?.start) + Number(f.position) - 1
              ) +
              this.getSingleBaseWidth() / 4
            },${this.layoutObj?.getFeatureYPos(f.feature as Feature)})`
        );

      fragmentG
        .selectAll<SVGRectElement, Feature>("rect.outer-rectangle")
        .attr("width", (f) =>
          Math.max(
            0,
            this.getSingleBaseWidth() *
              (f?.end && f?.start ? f.end - f.start + 1 : 1)
          )
        )
        .attr("height", this.layoutObj?.getFeatureHeight() || 0)
        .attr(
          "transform",
          (f) =>
            `translate(${this.getXFromSeqPosition(f.start || 0)},${
              this.layoutObj?.getFeatureYPos(f.feature as Feature) || 0
            })`
        );
    }
    this.updateHighlight();

    this.renderMarginOnGroup(this.margins);
  }

  protected updateHighlight() {
    if (!this.#highlighted) return;
    const highlights = this.#highlighted
      .selectAll<
        SVGRectElement,
        {
          start: number;
          end: number;
        }[]
      >("rect")
      .data(this.highlightedRegion.segments);

    highlights
      .enter()
      .append("rect")
      .style("pointer-events", "none")
      .merge(highlights)
      .attr("fill", this["highlight-color"])
      .attr("height", this.height)
      .attr("x", (d) => this.getXFromSeqPosition(d.start))
      .attr("width", (d) =>
        Math.max(0, this.getSingleBaseWidth() * (d.end - d.start + 1))
      );

    highlights.exit().remove();
  }

  zoomRefreshed() {
    if (this.getWidthWithMargins() > 0) this.refresh();
  }

  firstUpdated() {
    this.createTrack();
  }

  render() {
    return html`<svg class="container"></svg>`;
  }
}

export default NightingaleTrack;

export { DefaultLayout };
export { getColorByType };
